import { useMemo } from "react";
import { useSelector } from "src/redux/hooks";

import { OVERLAY_PREF } from "../constants";

interface LiftingEquipmentTargets {
  distinct?: string;
  certificates?: string;
}

interface Props {
  uuid: string;
  sectionIndex?: number;
  rowIndex?: number;
  rowUuid?: string;
  sharedIndex?: number;
  subSectionIndex?: number;
}

const getOverlayTargetId = (uuid: string, targetId?: string) => {
  if (!targetId) return undefined;

  return uuid.startsWith(OVERLAY_PREF) && !targetId.startsWith(OVERLAY_PREF)
    ? `${OVERLAY_PREF}${targetId}`
    : targetId;
};

const useLiftingEquipmentTargets = ({
  uuid,
  sectionIndex,
  rowIndex,
  rowUuid,
  sharedIndex,
  subSectionIndex,
}: Props): LiftingEquipmentTargets => {
  const sections = useSelector(state => state.generatorSlice.lbentry.sections);

  return useMemo(() => {
    if (typeof sectionIndex !== "number") return {};

    const currentSection =
      sections[sectionIndex]?.shared &&
      typeof sharedIndex === "number" &&
      sections[sectionIndex]?.sections
        ? sections[sectionIndex]?.sections?.[sharedIndex]
        : sections[sectionIndex];

    if (!currentSection) return {};

    let currentCols: Record<string, any> = {};

    const regRow = currentSection.rows?.[rowIndex ?? 0];
    if (regRow?.cols) {
      currentCols = regRow.cols;
    } else if (regRow?.subrows) {
      const bareUuid = uuid.startsWith(OVERLAY_PREF)
        ? uuid.slice(OVERLAY_PREF.length)
        : uuid;
      const matchingSubrow = (regRow.subrows as any[]).find(
        sub => sub.cols && (bareUuid in sub.cols || uuid in sub.cols),
      );
      if (matchingSubrow?.cols) {
        currentCols = matchingSubrow.cols;
      }
    } else if (
      currentSection.subSections &&
      typeof subSectionIndex === "number"
    ) {
      const sub = currentSection.subSections[subSectionIndex] as any;
      const subRow = sub?.rows?.[rowIndex ?? 0];

      if (subRow?.items && rowUuid) {
        const items = subRow.items;
        currentCols = items[rowUuid].cols;
      }
    }

    const distinctTargetId = Object.entries(currentCols).find(
      ([, col]: [string, any]) => col?.tag === "LQ_Distinct",
    )?.[0];

    const certTargetId = Object.entries(currentCols).find(
      ([, col]: [string, any]) => col?.tag === "LQ_CertNums",
    )?.[0];

    return {
      distinct: getOverlayTargetId(uuid, distinctTargetId),
      certificates: getOverlayTargetId(uuid, certTargetId),
    };
  }, [
    rowIndex,
    rowUuid,
    sectionIndex,
    sections,
    sharedIndex,
    subSectionIndex,
    uuid,
  ]);
};

export default useLiftingEquipmentTargets;
